import { Button, Stack, TextField } from '@mui/material'
import { format } from 'date-fns'
import { useEffect, useMemo, useState } from 'react'

import { editVisitorPath, routes } from '~/features/routes'
import { Link } from 'react-router'
import { GetVisitorFilters, useGetVisitors } from '~/features/hooks/useGetVisitors'
import { CustomDataGrid } from '~/ui/components/CustomDataGrid'
import { GridColDef, GridRowParams } from '@mui/x-data-grid'
import { Visitor, VisitorWithAgeGroup } from '~/supabase/types'
import { ACTION_ROW_CLASSNAME } from '~/ui/styles/data-grid'
import { FetchedInventory, useGetInventory } from '~/features/hooks/useGetInventory'
import { funnel } from 'remeda'
import { useSearchVisitors } from '~/features/visitors/hooks/useSearchVisitors'
import { useDateRangePicker } from '~/ui/hooks/useDateRangePicker'

export const VisitorList = () => {
  const { dates, DateRangePicker } = useDateRangePicker()
  const [search, setSearch] = useState('')
  const [searchResults, setSearchResults] = useState<VisitorWithAgeGroup[] | null>(null)
  const { mutateAsync: searchVisitors } = useSearchVisitors()

  const debouncedSearch = useMemo(
    () =>
      funnel(
        async () => {
          if (search.length > 2) {
            const result = await searchVisitors(search)
            setSearchResults(result)
          } else setSearchResults(null)
        },
        {
          minQuietPeriodMs: 200,
        }
      ),
    [search, searchVisitors]
  )

  const filters = useMemo<GetVisitorFilters>(() => {
    if (dates.start && dates.end)
      return [
        { operator: 'lt', column: 'created_at', value: dates.end.toISOString() },
        { operator: 'gt', column: 'created_at', value: dates.start.toISOString() },
      ]

    return []
  }, [dates.end, dates.start])

  const {
    data: visitors,
    isLoading: isVisitorsLoading,
    isFetching,
  } = useGetVisitors({
    filters: filters.length > 0 ? filters : undefined,
    keepPreviousData: true,
  })

  const { data: inventory, isLoading: isInventoryLoading } = useGetInventory()

  const inventoryMap = useMemo<Map<string, FetchedInventory>>(() => {
    if (!inventory) return new Map()
    return new Map(inventory.map((item) => [item.id.toString(), item]))
  }, [inventory])

  const columns = useMemo<GridColDef<Omit<Visitor, 'products'>>[]>(() => {
    return [
      {
        field: 'created_at',
        headerName: 'Date',
        filterable: false,
        renderCell: (params) => format(new Date(params.value), 'dd/MM'),
      },
      { field: 'agency', headerName: 'Agency', type: 'string', width: 120 },
      { field: 'country', headerName: 'Country', type: 'string' },
      { field: 'info', headerName: 'Info', flex: 1, type: 'string' },
      { field: 'total', headerName: 'Total', type: 'number' },
      { field: 'pax', headerName: 'Pax', width: 30, type: 'number' },
      {
        field: 'product_ids',
        headerName: 'Products',
        renderCell: (params) => {
          return params.row.product_ids
            ?.map((id) => {
              const product = inventoryMap.get(id)
              return product?.models_v2.product_type === 'ring'
                ? `${product?.stock_code_v2} - size ${product?.size}`
                : product?.stock_code_v2
            })
            .join(', ')
        },
        flex: 1,
      },
      {
        resizable: false,
        width: 120,
        field: 'actions',
        type: 'actions',
        getActions: (params: GridRowParams<Visitor>) => {
          return [
            <Button
              key="link-button"
              component={Link}
              to={editVisitorPath(params.row.id)}
              size="small"
              className={ACTION_ROW_CLASSNAME}
              sx={{
                color: 'text.secondary',
                textTransform: 'none',
                display: 'none',
                alignSelf: 'center',
              }}
            >
              Details
            </Button>,
          ]
        },
      },
    ]
  }, [inventoryMap])

  const rows = useMemo(() => {
    const list = searchResults || visitors

    return list ?? []
  }, [searchResults, visitors])

  const isLoading = isVisitorsLoading || isInventoryLoading || isFetching

  useEffect(() => {
    debouncedSearch.call()
  }, [debouncedSearch])

  return (
    <Stack overflow="hidden" flex={1}>
      <Stack sx={{ overflowY: 'hidden', height: 1 }}>
        <Stack
          direction={{ xs: 'column-reverse', sm: 'row' }}
          my={2}
          gap={2}
          justifyContent={{ xs: 'flex-end' }}
        >
          {DateRangePicker}

          <Button
            component={Link}
            to={routes.createVisitor.route}
            sx={{ alignSelf: { sm: 'flex-start' } }}
            variant="contained"
          >
            Create new
          </Button>
        </Stack>
        <TextField
          label="Search"
          placeholder="Search min 3 characters"
          size="small"
          onChange={(e) => setSearch(e.target.value)}
          value={search}
          sx={{ maxWidth: 200, ml: 'auto' }}
        />
        <CustomDataGrid columns={columns} rows={rows} loading={isLoading} />
      </Stack>
    </Stack>
  )
}
